import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, GitBranch, Send, Search } from "lucide-react";

/**
 * Retroactively wire an orphan child (addendum / renewal / amendment) to its
 * parent MSA. Search hits /org/contracts/search which already returns folder
 * breadcrumbs so the picker shows enough context to disambiguate vendors.
 */
export default function LinkParentModal({ open, contract, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [pick, setPick] = useState(null);
  const [rel, setRel] = useState("addendum");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ(""); setResults([]); setPick(null); setRel("addendum");
  }, [open, contract?.id]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return; }
    const c = new AbortController();
    const t = setTimeout(() => {
      api.get(`/org/contracts/search?q=${encodeURIComponent(q.trim())}&status=all`, { signal: c.signal })
        .then(r => setResults((r.data || []).filter(x => x.id !== contract?.id).slice(0, 8)))
        .catch(() => {});
    }, 250);
    return () => { clearTimeout(t); c.abort(); };
  }, [q, open, contract?.id]);

  if (!open || !contract) return null;

  const submit = async () => {
    if (!pick) return toast.error("Pick a parent contract");
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.id}/link-parent`, {
        parent_contract_id: pick.id, relation: rel,
      });
      toast.success(`Linked as ${rel} of #${pick.contract_number}`);
      onDone && onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center" onClick={onClose}
         data-testid="link-parent-modal">
      <div className="bg-white rounded-md w-[540px] max-w-[92vw] shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-[#005289]" />
          <div className="font-bold text-slate-900">Link #{contract.contract_number} to a parent</div>
          <button onClick={onClose} className="ml-auto text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Search parent (MSA / master contract)</span>
            <div className="relative">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={e => { setQ(e.target.value); setPick(null); }}
                     data-testid="link-parent-search"
                     placeholder="Vendor name, contract number…"
                     className="w-full h-9 pl-7 pr-3 text-sm border border-slate-200 rounded-md focus:border-[#005289]" />
            </div>
          </label>
          {q.trim().length >= 2 && (
            <div className="max-h-56 overflow-y-auto border border-slate-100 rounded" data-testid="link-parent-results">
              {results.length === 0 ? (
                <div className="p-3 text-xs text-slate-400 text-center italic">No matches. Keep typing…</div>
              ) : results.map(r => (
                <button key={r.id} onClick={() => setPick(r)} type="button"
                        data-testid={`link-parent-option-${r.contract_number}`}
                        className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 flex items-start gap-2 ${pick?.id === r.id ? "bg-[#005289]/10 border-l-2 border-l-[#005289]" : "hover:bg-slate-50"}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-mono text-slate-500">#{r.contract_number}</span>
                      <span className="font-semibold text-slate-900 truncate">{r.vendor_legal_name}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{r.folder_path} · {(r.stage || "").replace(/_/g,' ')}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Relation</span>
            <select value={rel} onChange={e => setRel(e.target.value)} data-testid="link-parent-rel"
                    className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md">
              <option value="addendum">Addendum</option>
              <option value="renewal">Renewal</option>
              <option value="amendment">Amendment</option>
            </select>
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2">
          <button onClick={onClose} className="h-9 px-3 text-xs border border-slate-200 rounded">Cancel</button>
          <button onClick={submit} disabled={busy || !pick} data-testid="link-parent-submit"
                  className="ml-auto h-9 px-4 rounded text-xs font-semibold bg-[#005289] text-white flex items-center gap-1 disabled:opacity-50">
            <Send className="w-3 h-3" /> {busy ? "Linking…" : "Link"}
          </button>
        </div>
      </div>
    </div>
  );
}
